import {
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/solid';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { useEffect, useMemo, useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';

import {
  findActiveByPath,
  findToolBySlug,
  toolCategories,
} from '@/constants/toolCategories';

import NavBadge from './NavBadge';
import ToolsNavTemplate, { ToolsNavTemplateInfo } from './ToolsNavTemplate';

interface ToolsNavProps {
  /**
   * Slug of the current tool (preferred — see `toolCategories`). When omitted,
   * the active tool is derived from the current route.
   */
  activeToolSlug?: string;
  /** Override the title shown in the context row. Defaults to the tool's name. */
  title?: string;
  /** Optional help/docs URL — renders a (?) icon next to the title when set. */
  helpHref?: string;
  /** Target for the "‹ Tools" breadcrumb. Defaults to /presets. */
  allToolsHref?: string;
  /** Right-aligned actions for the context row (e.g. a Templates button). */
  actions?: React.ReactNode;
  /** Template the tool was opened with — replaces the context card with the
   *  template's own preview while set. */
  template?: ToolsNavTemplateInfo | null;
  /** Set false to hide the second (tool context) row and show only the pills. */
  showToolBar?: boolean;
  /** Overrides the wrapper styling (padding, borders). */
  className?: string;
}

/**
 * Two-tier inter-tool navigation: a row of category pills (each opens a
 * dropdown of that category's tools) over a contextual bar showing the current
 * tool, a back-to-all-tools breadcrumb and page-supplied actions.
 *
 * Designed to sit at the top of a tool's controls column. Reusable across pages
 * via props; sources its data from the shared `toolCategories` taxonomy.
 */
const ToolsNav: React.FC<ToolsNavProps> = ({
  activeToolSlug,
  title,
  helpHref,
  allToolsHref = '/presets',
  actions,
  template,
  showToolBar = true,
  className,
}) => {
  const { t } = useLocale('dashboard');
  const router = useRouter();
  const navRef = useRef<HTMLDivElement>(null);
  const [openCategory, setOpenCategory] = useState<string | null>(null);

  const active = useMemo(
    () => findToolBySlug(activeToolSlug) ?? findActiveByPath(router.asPath),
    [activeToolSlug, router.asPath],
  );
  const activeCategorySlug = active?.category.slug;
  const activeToolSlugResolved = active?.tool.slug;
  const displayTitle = title ?? active?.tool.name;

  const dropdown = useMemo(
    () => toolCategories.find((c) => c.slug === openCategory),
    [openCategory],
  );

  // Close the dropdown on outside click / Escape.
  useEffect(() => {
    if (!openCategory) return;

    const handlePointer = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenCategory(null);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenCategory(null);
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [openCategory]);

  // Close the dropdown when navigating to a new tool.
  useEffect(() => {
    setOpenCategory(null);
  }, [router.asPath]);

  return (
    <div ref={navRef} className={clsxm('px-4 pb-4 pt-4 sm:px-6', className)}>
      {/* Tier 1 — category pills. NarrowDashboardLayout's floating menu
          button sits over this row's start below lg; the offset makes room. */}
      <div className='relative ms-[var(--floating-menu-offset,0px)]'>
        <div className='scrollbar-hide inline-flex max-w-full items-center gap-1 overflow-x-auto'>
          {toolCategories.map((category) => {
            const isActive = category.slug === activeCategorySlug;
            const isOpen = category.slug === openCategory;

            return (
              <button
                key={category.slug}
                type='button'
                onClick={() => setOpenCategory(isOpen ? null : category.slug)}
                aria-haspopup='menu'
                aria-expanded={isOpen}
                className={clsxm(
                  'flex flex-shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-gray-100 text-gray-900 dark:bg-white/10 dark:text-white'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-slate-200',
                )}
              >
                {t(category.title)}
                <ChevronDownIcon
                  className={clsxm(
                    'h-3.5 w-3.5 transition-transform duration-200',
                    isOpen && 'rotate-180',
                  )}
                />
              </button>
            );
          })}
        </div>

        {/* Shared dropdown panel (anchored to the nav, never overflows the column) */}
        {dropdown && (
          <div
            role='menu'
            className='absolute start-0 top-full z-50 mt-1.5 w-64 max-w-full rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-900'
          >
            {dropdown.tools.map((tool) => {
              const isCurrent = tool.slug === activeToolSlugResolved;

              return (
                <Link
                  key={tool.slug}
                  href={tool.href}
                  role='menuitem'
                  onClick={() => setOpenCategory(null)}
                  className={clsxm(
                    'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors',
                    isCurrent
                      ? 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-500/10 dark:text-primary-300'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-800',
                  )}
                >
                  <tool.icon className='h-4 w-4 flex-shrink-0 opacity-70' />
                  <span className='flex-1 truncate'>{t(tool.name)}</span>
                  {tool.badge && <NavBadge type={tool.badge} />}
                  {isCurrent && (
                    <CheckIcon className='h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400' />
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Tier 2 — the template the tool was opened with, or the tool itself */}
      {showToolBar &&
        (template ? (
          <ToolsNavTemplate key={template.previewUrl} {...template} />
        ) : (
          <div className='mt-2.5 flex items-center justify-between gap-3 rounded-xl border border-gray-200/80 bg-gray-50 px-3.5 py-2.5 dark:border-white/10 dark:bg-white/5'>
            <div className='min-w-0'>
              <Link
                href={allToolsHref}
                className='inline-flex items-center gap-0.5 text-xs font-medium text-gray-400 transition-colors hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300'
              >
                <ChevronLeftIcon className='h-3.5 w-3.5 rtl:rotate-180' />
                {t('Tools')}
              </Link>
              <div className='mt-0.5 flex items-center gap-1.5'>
                <h2 className='truncate text-base font-semibold tracking-tight text-gray-900 dark:text-white'>
                  {displayTitle ? t(displayTitle) : t('Tools')}
                </h2>
                {helpHref && (
                  <a
                    href={helpHref}
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label={t('Learn more')}
                    className='flex-shrink-0 text-gray-400 transition-colors hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300'
                  >
                    <QuestionMarkCircleIcon className='h-4 w-4' />
                  </a>
                )}
              </div>
            </div>

            {actions && (
              <div className='flex flex-shrink-0 items-center gap-2'>
                {actions}
              </div>
            )}
          </div>
        ))}
    </div>
  );
};

export default ToolsNav;
